import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import clsx from 'clsx';
import { TabContent } from './TabContent';
import { TabsProps, TabDataItem, GroupedTabData } from './Tabs.types';
import { SVGIcon } from '../SVGIcon';

/**
 * Funkcja pomocnicza do grupowania danych według title z prefiksem instancji
 */
const groupDataByTitle = (
  data: TabDataItem[],
  prefix: string,
): GroupedTabData[] => {
  const grouped = data.reduce(
    (acc, item, index) => {
      const baseTabId = item.title.toLowerCase().replace(/\s+/g, '-');
      const tabId = `${prefix}-${baseTabId}`;

      if (!acc[tabId]) {
        acc[tabId] = {
          tabId,
          title: item.title,
          items: [],
          icon: item.icon,
        };
      }

      acc[tabId].items.push({
        id: item.id || `${tabId}-${index}`,
        label: item.label,
        value: item.value,
      });

      return acc;
    },
    {} as Record<string, GroupedTabData>,
  );

  return Object.values(grouped);
};

export const Tabs: React.FC<TabsProps> = ({
  data,
  defaultValue,
  onValueChange,
  className,
  centered = false,
  prefix,
  contentSize = 'small',
}) => {
  // Unikalny identyfikator dla tej instancji komponentu

  // Grupowanie danych według title z prefiksem instancji
  const groupedData = useMemo(
    () => groupDataByTitle(data, prefix),
    [data, prefix],
  );

  // Domyślny aktywny tab (pierwszy lub podany w defaultValue)
  // Jeśli defaultValue jest podane, sprawdź czy zawiera prefiks instancji
  // Jeśli nie, spróbuj znaleźć pasujący tab na podstawie baseTabId
  const getInitialValue = useCallback(() => {
    if (!defaultValue) {
      return groupedData[0]?.tabId || '';
    }

    // Jeśli defaultValue zawiera prefiks instancji, użyj go bezpośrednio
    if (defaultValue.startsWith(prefix)) {
      return defaultValue;
    }

    // W przeciwnym razie spróbuj znaleźć pasujący tab na podstawie baseTabId
    const matchingTab = groupedData.find(
      (group) =>
        group.tabId === `${prefix}-${defaultValue}` ||
        group.tabId.endsWith(`-${defaultValue}`),
    );

    return matchingTab?.tabId || groupedData[0]?.tabId || '';
  }, [defaultValue, groupedData, prefix]);

  const initialValue = useMemo(() => getInitialValue(), [getInitialValue]);
  const [activeTab, setActiveTab] = useState<string>(initialValue);

  // Referencje do sekcji dla Intersection Observer
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Obsługa zmiany tab'a (ręczne kliknięcie)
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    onValueChange?.(tabId);

    // Scrolluj do odpowiedniej sekcji
    const targetSection = sectionRefs.current.get(tabId);
    if (targetSection) {
      // Oblicz pozycję tak, aby sekcja była w obszarze 10% od topu ekranu
      const headerOffset = window.innerHeight * 0.1; // 10% wysokości ekranu
      const elementPosition = targetSection.offsetTop - headerOffset;

      window.scrollTo({
        top: Math.max(0, elementPosition),
        behavior: 'smooth',
      });
    }
  };

  // Callback dla Intersection Observer
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      // Znajdź wszystkie przecinające się sekcje
      const intersectingSections = entries
        .filter((entry) => entry.isIntersecting)
        .map((entry) => ({
          tabId: entry.target.getAttribute('data-tab-id') || '',
          ratio: entry.intersectionRatio,
          boundingRect: entry.boundingClientRect,
        }))
        .filter((section) => section.tabId);

      if (intersectingSections.length === 0) return;

      // Znajdź sekcję najbliższą górze viewportu (najbardziej widoczną)
      const mostVisibleSection = intersectingSections.reduce((prev, current) => {
        // Preferuj sekcję, która jest najbliżej góry viewportu
        if (current.boundingRect.top < prev.boundingRect.top) {
          return current;
        }
        // Jeśli są na tej samej wysokości, wybierz tę z wyższym intersectionRatio
        if (
          current.boundingRect.top === prev.boundingRect.top &&
          current.ratio > prev.ratio
        ) {
          return current;
        }
        return prev;
      });

      // Przełącz aktywny tab tylko jeśli znaleziono widoczną sekcję
      if (
        mostVisibleSection.tabId &&
        mostVisibleSection.tabId !== activeTab
      ) {
        setActiveTab(mostVisibleSection.tabId);
        onValueChange?.(mostVisibleSection.tabId);
      }
    },
    [activeTab, onValueChange],
  );

  // Inicjalizacja Intersection Observer
  useEffect(() => {
    if (groupedData.length === 0) return;

    // Konfiguracja observer - używamy wartości bezwzględnych, które działają lepiej na małych ekranach
    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '-80px 0px -70% 0px', // 80px od góry (sticky navbar + margines), 70% od dołu
      threshold: [0, 0.1, 0.2, 0.3, 0.5], // Różne poziomy widoczności, włącznie z 0 dla pierwszej sekcji
    };

    // Utwórz nowy observer
    observerRef.current = new IntersectionObserver(handleIntersection, options);

    // Zbierz aktualne tabId z groupedData
    const currentTabIds = new Set(groupedData.map((group) => group.tabId));

    // Usuń stare referencje, które nie są już w groupedData
    sectionRefs.current.forEach((_, tabId) => {
      if (!currentTabIds.has(tabId)) {
        sectionRefs.current.delete(tabId);
      }
    });

    // Użyj requestAnimationFrame aby upewnić się, że DOM jest gotowy
    const observeSections = () => {
      // Obserwuj wszystkie sekcje należące do tej instancji
      sectionRefs.current.forEach((section, tabId) => {
        // Obserwuj tylko sekcje należące do tej instancji (z prefiksem instanceId)
        if (section && observerRef.current && tabId.startsWith(prefix)) {
          observerRef.current.observe(section);
        }
      });
    };

    // Opóźnij obserwację do następnej klatki, aby DOM był gotowy
    const rafId = requestAnimationFrame(observeSections);

    // Cleanup
    return () => {
      cancelAnimationFrame(rafId);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [groupedData, handleIntersection, prefix]);

  // Funkcja do ustawiania ref dla sekcji
  const setSectionRef = useCallback((tabId: string) => {
    return (element: HTMLDivElement | null) => {
      if (element) {
        sectionRefs.current.set(tabId, element);
      } else {
        sectionRefs.current.delete(tabId);
      }
    };
  }, []);

  if (groupedData.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-grayText">
        Brak danych do wyświetlenia
      </div>
    );
  }

  return (
    <div className={clsx('w-full', className)}>
      {/* Tabs Navigation */}
      <div
        className={clsx(
          'sticky top-0 flex gap-2 overflow-x-auto bg-white py-4 shadow-sm',
          centered && 'justify-center',
          !centered && 'justify-start',
        )}
        role="tablist"
      >
        {groupedData.map((group) => {
          const isActive = activeTab === group.tabId;

          return (
            <button
              key={group.tabId}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${group.tabId}`}
              id={`tab-${group.tabId}`}
              className={clsx(
                'relative flex-1 font-inter font-medium transition-all duration-200 ease-in-out',
                'h-[36px] rounded-[6px] px-3 py-2 text-center text-xs',
                'flex min-w-fit items-center md:max-w-[160px] lg:min-w-[100px]',
                {
                  // Active tab styles - design z Figmy
                  'bg-primary fill-white text-white': isActive,
                  // Inactive tab styles
                  'hover:fill:bg-gray-50 bg-white text-text-muted hover:bg-gray-50':
                    !isActive,
                },
                'hover:fill-bg-gray-50',
              )}
              onClick={() => handleTabChange(group.tabId)}
            >
              <SVGIcon name={group.icon} className="mr-3" />
              {group.title}
            </button>
          );
        })}
      </div>

      {/* Tabs Content */}
      <div className="mt-6">
        {groupedData.map((group) => (
          <div
            key={group.tabId}
            ref={setSectionRef(group.tabId)}
            data-tab-id={group.tabId}
            id={`${group.tabId}`}
            role="tabpanel"
            aria-labelledby={`tab-${group.tabId}`}
            className="mb-11"
          >
            <TabContent
              items={group.items}
              title={group.title}
              icon={group.icon}
              size={contentSize}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
